import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  GraphicEq as WaveIcon,
  Search as SearchIcon,
  Tune as FiltersIcon,
  SwapVert as SortIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ChatBubbleOutline as ConversationalIcon,
  MenuBookOutlined as NarrationIcon,
  TheaterComedyOutlined as CharactersIcon,
  SmartphoneOutlined as SocialIcon,
  SchoolOutlined as EducationalIcon,
  CampaignOutlined as AdIcon,
  Language as LanguageIcon,
  RecordVoiceOver as AccentIcon,
  ContentCopy as CopyIcon,
  PlaylistAdd as AddToMineIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import VoiceCloneModal from "./VoiceCloneModal";

const USE_CASE_CHIPS = [
  { id: "conversational", label: "Conversational", icon: ConversationalIcon },
  { id: "narration", label: "Narration", icon: NarrationIcon },
  { id: "characters", label: "Characters", icon: CharactersIcon },
  { id: "social_media", label: "Social Media", icon: SocialIcon },
  { id: "educational", label: "Educational", icon: EducationalIcon },
  { id: "advertisement", label: "Advertisement", icon: AdIcon },
];

const LANGUAGE_OPTIONS = [
  { value: "", label: "Language" },
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese" },
  { value: "hi", label: "Hindi" },
  { value: "ar", label: "Arabic" },
];

const ACCENT_OPTIONS = [
  { value: "", label: "Accent" },
  { value: "american", label: "American" },
  { value: "british", label: "British" },
  { value: "australian", label: "Australian" },
  { value: "irish", label: "Irish" },
  { value: "indian", label: "Indian" },
  { value: "african", label: "African" },
];

const SORT_OPTIONS = [
  { value: "trending", label: "Trending" },
  { value: "cloned_by_count", label: "Most cloned" },
  { value: "created_date", label: "Newest" },
];

function languageLabel(code) {
  if (!code) return "English";
  const hit = LANGUAGE_OPTIONS.find((o) => o.value === String(code).toLowerCase());
  if (hit?.value) return hit.label;
  const raw = String(code);
  return raw.length <= 3 ? raw.toUpperCase() : raw;
}

function avatarHue(name) {
  let h = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % 360;
  return h;
}

function VoiceCard({ voice, playingId, onPlay, onCopy, onAdd, lang, theme }) {
  const id = voice.voice_id;
  const playing = playingId === id;
  const title = voice.name || lang?.voicesUnnamed || "Untitled voice";
  const desc = voice.description || voice.use_case || voice.category || "";
  const useCase = voice.use_case || voice.category || "";
  const langCode = voice.language || voice.labels?.language || "en";
  const hue = avatarHue(title);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        bgcolor: "background.paper",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: alpha(theme.palette.text.primary, 0.28),
          boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
        },
      }}
    >
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Avatar
          src={voice.image_url || undefined}
          alt=""
          sx={{
            width: 48,
            height: 48,
            bgcolor: `hsl(${hue} 42% 42%)`,
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          {String(title).slice(0, 1).toUpperCase()}
        </Avatar>
        <IconButton
          size="small"
          onClick={() => onPlay(voice)}
          disabled={!voice.preview_url}
          sx={{
            position: "absolute",
            right: -4,
            bottom: -4,
            width: 26,
            height: 26,
            bgcolor: theme.palette.mode === "dark" ? "#111" : "#111",
            color: "#fff",
            border: `2px solid ${theme.palette.background.paper}`,
            "&:hover": { bgcolor: "#000" },
            "&.Mui-disabled": { bgcolor: alpha("#111", 0.35), color: "#fff" },
          }}
        >
          {playing ? <PauseIcon sx={{ fontSize: 14 }} /> : <PlayIcon sx={{ fontSize: 14 }} />}
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
          {desc ? (
            <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
              {` — ${String(desc).slice(0, 72)}${String(desc).length > 72 ? "…" : ""}`}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {useCase ? (
            <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "capitalize" }}>
              {String(useCase).replace(/_/g, " ")}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {languageLabel(langCode)}
            </Typography>
            {voice.accent ? (
              <Chip
                size="small"
                label={String(voice.accent)}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }}
              />
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
        {onAdd ? (
          <IconButton
            size="small"
            title={lang?.voicesAddToMine || "Add to My Voices"}
            onClick={() => onAdd(voice)}
          >
            <AddToMineIcon fontSize="small" />
          </IconButton>
        ) : null}
        <IconButton
          size="small"
          title={lang?.voicesOptCopyId || "Copy Voice ID"}
          onClick={() => onCopy(id)}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

export default function Voices({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const isDark = theme.palette.mode === "dark";

  const [tab, setTab] = React.useState("explore");
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [language, setLanguage] = React.useState("");
  const [accent, setAccent] = React.useState("");
  const [useCase, setUseCase] = React.useState("");
  const [sort, setSort] = React.useState("trending");
  const [showFilters, setShowFilters] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [voices, setVoices] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [cloneOpen, setCloneOpen] = React.useState(false);
  const [playingId, setPlayingId] = React.useState(null);
  const audioRef = React.useRef(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(t);
  }, [search]);

  const stopPreview = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  }, []);

  React.useEffect(() => () => stopPreview(), [stopPreview]);

  const loadExplore = React.useCallback(
    async (pageNum = 0, append = false) => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        qs.set("page", String(pageNum));
        qs.set("page_size", "24");
        qs.set("sort", sort || "trending");
        if (debouncedSearch) qs.set("search", debouncedSearch);
        if (language) qs.set("language", language);
        if (accent) qs.set("accent", accent);
        if (useCase) qs.set("use_cases", useCase);

        const res = await hitAxios({
          path: `/api/voice-changer/shared-voices?${qs.toString()}`,
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        });
        if (!res?.data?.success) {
          showSnack(res?.data?.msg || lang?.voicesLoadError || "Could not load voices.", false);
          if (!append) setVoices([]);
          return;
        }
        const next = res.data.data?.voices || [];
        setVoices((prev) => (append ? [...prev, ...next] : next));
        setHasMore(Boolean(res.data.data?.has_more));
        setPage(pageNum);
      } finally {
        setLoading(false);
      }
    },
    [accent, debouncedSearch, hitAxios, lang, language, showSnack, sort, useCase],
  );

  const loadMine = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: "/api/voice-changer/voices",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        showSnack(res?.data?.msg || lang?.voicesLoadError || "Could not load voices.", false);
        setVoices([]);
        return;
      }
      const all = res.data.data?.voices || [];
      const mapped = all.map((v) => ({
        ...v,
        description: v.description || v.labels?.description || v.labels?.descriptive || "",
        use_case: v.use_case || v.labels?.use_case || v.category,
        language: v.language || v.labels?.language,
        accent: v.accent || v.labels?.accent,
      }));
      const mine = mapped.filter((v) => {
        const cat = String(v.category || "").toLowerCase();
        return cat === "cloned" || cat === "generated" || cat === "professional" || !cat || cat === "premade";
      });
      // Prefer cloned first; keep premade at end if present.
      mine.sort((a, b) => {
        const ac = String(a.category || "");
        const bc = String(b.category || "");
        if (ac === "cloned" && bc !== "cloned") return -1;
        if (bc === "cloned" && ac !== "cloned") return 1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      const q = debouncedSearch.toLowerCase();
      setVoices(
        q
          ? mine.filter((v) =>
              `${v.name || ""} ${v.description || ""} ${v.category || ""}`
                .toLowerCase()
                .includes(q),
            )
          : mine,
      );
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, hitAxios, lang, showSnack]);

  React.useEffect(() => {
    stopPreview();
    if (tab === "explore") loadExplore(0, false);
    else loadMine();
  }, [tab, loadExplore, loadMine, stopPreview]);

  const onPlay = (voice) => {
    const url = voice?.preview_url;
    if (!url) {
      showSnack(lang?.voicesPreviewError || "Could not play preview.", false);
      return;
    }
    if (playingId === voice.voice_id) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingId(voice.voice_id);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => {
      setPlayingId(null);
      showSnack(lang?.voicesPreviewError || "Could not play preview.", false);
    };
    audio.play().catch(() => {
      setPlayingId(null);
      showSnack(lang?.voicesPreviewError || "Could not play preview.", false);
    });
  };

  const onCopy = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id || ""));
      showSnack(lang?.voicesCopiedId || "Voice ID copied to clipboard.", true);
    } catch {
      showSnack(lang?.voicesCopyFailed || "Could not copy Voice ID.", false);
    }
  };

  const onAdd = async (voice) => {
    if (!voice?.public_owner_id || !voice?.voice_id) {
      showSnack(lang?.voicesAddFailed || "This voice cannot be added.", false);
      return;
    }
    const res = await hitAxios({
      path: "/api/voice-changer/shared-voices/add",
      post: true,
      admin: false,
      obj: {
        public_owner_id: voice.public_owner_id,
        voice_id: voice.voice_id,
        new_name: voice.name,
      },
    });
    if (res?.data?.success) {
      showSnack(lang?.voicesAddedMine || "Added to My Voices.", true);
    }
  };

  const chipSx = (active) => ({
    fontWeight: 600,
    height: 36,
    borderRadius: 999,
    px: 0.75,
    border: `1px solid ${active ? (isDark ? "#f5f5f5" : "#111") : alpha(theme.palette.divider, 0.95)}`,
    bgcolor: active ? (isDark ? "#f5f5f5" : "#111") : "transparent",
    color: active ? (isDark ? "#111" : "#fff") : "text.primary",
    "& .MuiChip-icon": { color: "inherit" },
    "&:hover": {
      bgcolor: active
        ? isDark
          ? "#e8e8e8"
          : "#1f1f1f"
        : alpha(theme.palette.text.primary, 0.04),
    },
  });

  const selectSx = {
    minWidth: 120,
    height: 36,
    borderRadius: 999,
    fontWeight: 600,
    fontSize: "0.8125rem",
    bgcolor: "transparent",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.95),
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        pb: 4,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
          {lang?.voicesTitle || "Voices"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCloneOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            px: 2,
            bgcolor: isDark ? "#f5f5f5" : "#111",
            color: isDark ? "#111" : "#fff",
            boxShadow: "none",
            "&:hover": { bgcolor: isDark ? "#fff" : "#000", boxShadow: "none" },
          }}
        >
          {lang?.voicesCreate || "Create Voice"}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
        <Chip
          icon={<WaveIcon sx={{ fontSize: 16 }} />}
          label={lang?.voicesTabExplore || "Explore"}
          onClick={() => setTab("explore")}
          sx={chipSx(tab === "explore")}
        />
        <Button
          onClick={() => setTab("mine")}
          sx={{
            textTransform: "none",
            fontWeight: tab === "mine" ? 700 : 600,
            color: tab === "mine" ? "text.primary" : "text.secondary",
            minWidth: 0,
            px: 1.25,
          }}
        >
          {lang?.voicesTabMine || "My Voices"}
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: 1.75, width: "100%", minWidth: 0 }}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === "explore"
              ? lang?.voicesSearchLibrary || "Search library voices..."
              : lang?.voicesSearchPlaceholder || "Search voices..."
          }
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: 999,
              bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.03),
            },
          }}
        />
        {tab === "explore" ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <IconButton
              onClick={() => setShowFilters((v) => !v)}
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                borderRadius: 2,
                width: 42,
                height: 42,
                flexShrink: 0,
                bgcolor: showFilters ? alpha(theme.palette.text.primary, 0.06) : "transparent",
              }}
              title={lang?.voicesFilters || "Filters"}
            >
              <FiltersIcon fontSize="small" />
            </IconButton>
            <FormControl size="small" sx={{ flexShrink: 0, minWidth: 132 }}>
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                }
                sx={{
                  ...selectSx,
                  minWidth: 132,
                  borderRadius: 2,
                  height: 42,
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        ) : null}
      </Stack>

      {tab === "explore" ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{
            mb: 2,
            alignItems: "center",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <FormControl size="small">
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <LanguageIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              }
              sx={selectSx}
            >
              {LANGUAGE_OPTIONS.map((o) => (
                <MenuItem key={o.value || "all"} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <AccentIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              }
              sx={selectSx}
            >
              {ACCENT_OPTIONS.map((o) => (
                <MenuItem key={o.value || "all"} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {USE_CASE_CHIPS.map((c) => {
            const Icon = c.icon;
            const active = useCase === c.id;
            return (
              <Chip
                key={c.id}
                icon={<Icon sx={{ fontSize: 16 }} />}
                label={c.label}
                onClick={() => setUseCase((prev) => (prev === c.id ? "" : c.id))}
                sx={chipSx(active)}
              />
            );
          })}
        </Stack>
      ) : null}

      {showFilters && tab === "explore" ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {lang?.voicesFilterHint ||
            "Filters apply to the ElevenLabs voice library. Clear Language / Accent / category chips to reset."}
        </Alert>
      ) : null}

      {tab === "explore" ? (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.75 }}>
          {lang?.voicesTrending || "Trending voices"}
        </Typography>
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.75 }}>
          {lang?.voicesMyHeading || "Your voices"}
        </Typography>
      )}

      {loading && !voices.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !voices.length ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {tab === "mine"
            ? lang?.voicesEmptyMine ||
              "No personal voices yet. Create a voice or add one from Explore."
            : lang?.voicesEmptyLibrary || "No voices match your filters."}
        </Alert>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            width: "100%",
            minWidth: 0,
          }}
        >
          {voices.map((voice) => (
            <VoiceCard
              key={`${voice.public_owner_id || "local"}:${voice.voice_id}`}
              voice={voice}
              playingId={playingId}
              onPlay={onPlay}
              onCopy={onCopy}
              onAdd={tab === "explore" ? onAdd : null}
              lang={lang}
              theme={theme}
            />
          ))}
        </Box>
      )}

      {tab === "explore" && hasMore ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => loadExplore(page + 1, true)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={18} /> : lang?.voicesLoadMore || "Load more"}
          </Button>
        </Box>
      ) : null}

      <VoiceCloneModal
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        lang={lang}
        showSnack={showSnack}
        hitAxios={hitAxios}
        onCreated={() => {
          setCloneOpen(false);
          setTab("mine");
          loadMine();
        }}
      />
    </Box>
  );
}
